import { FormDefinition, Section, TextField } from "@/types/form";
import { v4 as uuidv4 } from "uuid";

export type FormAction =
  | { type: "ADD_SECTION" }
  | {
    type: "ADD_FIELD";
    payload: { sectionId: string; previousFieldId: string | undefined };
  }
  | {
    type: "UPDATE_FIELD_LABEL";
    payload: { sectionId: string; fieldId: string; label: string };
  }
  | {
    type: "UPDATE_FIELD_TYPE";
    payload: {
      sectionId: string;
      fieldId: string;
      fieldType: string;
    };
  }
  | { type: "TOGGLE_REQUIRED"; payload: { sectionId: string; fieldId: string } }
  | { type: "TOGGLE_WITH_TIME"; payload: { sectionId: string; fieldId: string } }
  | {
    type: "UPDATE_FIELD_OPTION";
    payload: {
      sectionId: string;
      fieldId: string;
      optionIndex: number;
      value: string;
    };
  }
  | {
    type: "ADD_FIELD_OPTION";
    payload: { sectionId: string; fieldId: string };
  }
  | {
    type: "REMOVE_FIELD_OPTION";
    payload: {
      sectionId: string;
      fieldId: string;
      optionIndex: number;
    };
  }
  | {
    type: "DELETE_FIELD";
    payload: {
      sectionId: string;
      fieldId: string;
    };
  }
  | {
    type: "SET_NEXT_SECTION";
    payload: {
      sectionId: string;
      nextSectionId?: string;
    };
  }
  | {
    type: "UPDATE_SECTION_TITLE";
    payload: {
      sectionId: string;
      title: string;
    };
  }
  | {
    type: "TOGGLE_BRANCHING";
    payload: {
      sectionId: string;
      fieldId: string;
    };
  }
  | {
    type: "UPDATE_BRANCHING_OPTION";
    payload: {
      sectionId: string;
      fieldId: string;
      optionIndex: number;
      nextSectionId: string;
    };
  }
  | {
    type: "REMOVE_SECTION";
    payload: {
      sectionId: string;
    };
  }
  | {
    type: "TOGGLE_STARTING_SECTION";
    payload: {
      sectionId: string;
    };
  }
  | {
    type: "SET_TITLE_FIELD";
    payload: {
      fieldId: string;
    };
  }
  | {
    type: "MOVE_FIELD";
    payload: { fieldId: string; sectionId: string; up: boolean };
  }
  | {
    type: "SPLIT_SECTION";
    payload: {
      sectionId: string;
      fieldId: string;
    };
  }
  | {
    type: "REPLACE_FORM_DEFINITION";
    payload: FormDefinition;
  };

export function formAuthoringReducer(state: FormDefinition, action: FormAction): FormDefinition {
  switch (action.type) {
    case "ADD_SECTION":
      return {
        ...state,
        sections: [
          ...state.sections,
          {
            id: uuidv4(),
            title: "New Section",
            fields: [],
            isStartingSection: state.sections.length === 0,
          },
        ],
      };
    case "ADD_FIELD": {
      const newField: TextField = {
        id: uuidv4(),
        type: "text",
        label: "",
        required: false,
      };

      if (action.payload.previousFieldId === undefined) {
        return {
          ...state,
          sections: state.sections.map((section) =>
            section.id === action.payload.sectionId
              ? { ...section, fields: [newField, ...section.fields] }
              : section
          ),
        };
      }

      return {
        ...state,
        sections: state.sections.map((section) => {
          if (section.id !== action.payload.sectionId) {
            return section;
          }

          const newFields = [...section.fields];
          const previousFieldIndex = section.fields.findIndex(
            (field) => field.id === action.payload.previousFieldId
          );
          newFields.splice(previousFieldIndex + 1, 0, newField);

          return { ...section, fields: newFields };
        }),
      };
    }
    case "UPDATE_FIELD_LABEL":
      return {
        ...state,
        sections: state.sections.map((section) =>
          section.id === action.payload.sectionId
            ? {
              ...section,
              fields: section.fields.map((field) =>
                field.id === action.payload.fieldId
                  ? { ...field, label: action.payload.label }
                  : field
              ),
            }
            : section
        ),
      };
    case "UPDATE_FIELD_TYPE":
      return {
        ...state,
        sections: state.sections.map((section) => {
          if (section.id !== action.payload.sectionId) return section;
          const fieldIndex = section.fields.findIndex(
            (field) => field.id === action.payload.fieldId
          );
          if (fieldIndex === -1) return section;

          const newFields = [...section.fields];
          switch (action.payload.fieldType) {
            case "radio":
            case "checkbox":
              newFields[fieldIndex] = {
                ...newFields[fieldIndex],
                type: action.payload.fieldType,
                options: newFields[fieldIndex].options ?? ["Option 1", "Option 2"],
              };
              break;
            case "text":
            case "textarea":
            case "date":
            case "upload":
              newFields[fieldIndex] = {
                ...newFields[fieldIndex],
                type: action.payload.fieldType,
                options: undefined,
              };
              break;
            default:
              throw new Error("Invalid field type");
          }

          const branchingRadio =
            section.branchingRadio?.fieldId === action.payload.fieldId
              ? section.branchingRadio
              : undefined;

          return { ...section, fields: newFields, branchingRadio };
        }),
      };
    case "TOGGLE_REQUIRED":
      return {
        ...state,
        sections: state.sections.map((section) =>
          section.id === action.payload.sectionId
            ? {
              ...section,
              fields: section.fields.map((field) =>
                field.id === action.payload.fieldId
                  ? { ...field, required: !field.required }
                  : field
              ),
            }
            : section
        ),
      };
    case "TOGGLE_WITH_TIME":
      return {
        ...state,
        sections: state.sections.map((section) =>
          section.id === action.payload.sectionId
            ? {
              ...section,
              fields: section.fields.map((field) =>
                field.id === action.payload.fieldId && field.type === "date"
                  ? { ...field, withTime: !field.withTime }
                  : field
              ),
            }
            : section
        ),
      };
    case "UPDATE_FIELD_OPTION":
      return {
        ...state,
        sections: state.sections.map((section) => {
          if (section.id !== action.payload.sectionId) return section;
          const fieldIndex = section.fields.findIndex(
            (field) => field.id === action.payload.fieldId
          );
          if (fieldIndex === -1) return section;

          const field = section.fields[fieldIndex];
          if (field.type !== "radio" && field.type !== "checkbox") {
            throw new Error("Invalid field type");
          }

          const newFields = [...section.fields];
          const newOptions = [...(field.options || [])];
          newOptions[action.payload.optionIndex] = action.payload.value;
          newFields[fieldIndex] = { ...field, options: newOptions };
          return { ...section, fields: newFields };
        }),
      };
    case "ADD_FIELD_OPTION":
      return {
        ...state,
        sections: state.sections.map((section) => {
          if (section.id !== action.payload.sectionId) return section;
          const fieldIndex = section.fields.findIndex(
            (field) => field.id === action.payload.fieldId
          );
          if (fieldIndex === -1) return section;

          const field = section.fields[fieldIndex];
          if (field.type !== "radio" && field.type !== "checkbox") {
            throw new Error("Invalid field type");
          }

          const newFields = [...section.fields];
          const newOptions = [
            ...(field.options || []),
            `Option ${field.options ? field.options.length + 1 : 1}`,
          ];
          newFields[fieldIndex] = { ...field, options: newOptions };
          return { ...section, fields: newFields };
        }),
      };
    case "REMOVE_FIELD_OPTION":
      return {
        ...state,
        sections: state.sections.map((section) => {
          if (section.id !== action.payload.sectionId) return section;
          const fieldIndex = section.fields.findIndex(
            (field) => field.id === action.payload.fieldId
          );
          if (fieldIndex === -1) return section;

          const field = section.fields[fieldIndex];
          if (field.type !== "radio" && field.type !== "checkbox") {
            throw new Error("Invalid field type");
          }

          const newFields = [...section.fields];
          const newOptions =
            field.options?.filter(
              (_, index) => index !== action.payload.optionIndex
            ) || [];
          newFields[fieldIndex] = { ...field, options: newOptions };
          return { ...section, fields: newFields };
        }),
      };
    case "DELETE_FIELD":
      return {
        ...state,
        sections: state.sections.map((section) => {
          if (section.id !== action.payload.sectionId) return section;
          return {
            ...section,
            fields: section.fields.filter(
              (field) => field.id !== action.payload.fieldId
            ),
          };
        }),
      };
    case "SET_NEXT_SECTION":
      return {
        ...state,
        sections: state.sections.map((section) =>
          section.id === action.payload.sectionId
            ? { ...section, nextSectionId: action.payload.nextSectionId }
            : section
        ),
      };
    case "UPDATE_SECTION_TITLE":
      return {
        ...state,
        sections: state.sections.map((section) =>
          section.id === action.payload.sectionId
            ? { ...section, title: action.payload.title }
            : section
        ),
      };
    case "TOGGLE_BRANCHING":
      return {
        ...state,
        sections: state.sections.map((section) => {
          if (section.id !== action.payload.sectionId) {
            return section;
          }

          const enableBranching =
            section.branchingRadio?.fieldId !== action.payload.fieldId;
          const isRequired = section.fields.find(
            (field) => field.id === action.payload.fieldId
          )?.required;

          const newSection = {
            ...section,
            nextSectionId: undefined,
            branchingRadio: enableBranching
              ? {
                fieldId: action.payload.fieldId,
                branchingOptions: initializeBranchingOptions(
                  state.sections,
                  action.payload.fieldId,
                  section
                ),
              }
              : undefined,
          };

          if (enableBranching && !isRequired) {
            newSection.fields = newSection.fields.map((field) =>
              field.id === action.payload.fieldId
                ? { ...field, required: true }
                : field
            );
          }

          return newSection;
        }),
      };
    case "UPDATE_BRANCHING_OPTION":
      return {
        ...state,
        sections: state.sections.map((section) => {
          if (section.id !== action.payload.sectionId) return section;
          if (section.branchingRadio?.fieldId !== action.payload.fieldId)
            return section;
          const newBranchingOptions = [
            ...section.branchingRadio.branchingOptions,
          ];
          newBranchingOptions[action.payload.optionIndex] =
            action.payload.nextSectionId;

          return {
            ...section,
            branchingRadio: {
              ...section.branchingRadio,
              branchingOptions: newBranchingOptions,
            },
          };
        }),
      };
    case "REMOVE_SECTION":
      return {
        ...state,
        sections: state.sections.filter(
          (section) => section.id !== action.payload.sectionId
        ),
      };
    case "TOGGLE_STARTING_SECTION":
      return {
        ...state,
        sections: state.sections.map((section) =>
          section.id === action.payload.sectionId
            ? { ...section, isStartingSection: !section.isStartingSection }
            : section
        ),
      };
    case "SET_TITLE_FIELD": {
      const oldField = state.sections
        .flatMap((section) => section.fields)
        .find((field) => field.id === action.payload.fieldId);

      if (
        oldField?.required !== true &&
        state.titleFieldId === action.payload.fieldId
      ) {
        return {
          ...formAuthoringReducer(state, {
            type: "TOGGLE_REQUIRED",
            payload: {
              sectionId: state.sections[0].id,
              fieldId: action.payload.fieldId,
            },
          }),
          titleFieldId: action.payload.fieldId,
        };
      }

      return { ...state, titleFieldId: action.payload.fieldId };
    }
    case "MOVE_FIELD":
      return {
        ...state,
        sections: state.sections.map((section) => {
          if (section.id !== action.payload.sectionId) {
            return section;
          }

          const fieldIndex = section.fields.findIndex(
            (field) => field.id === action.payload.fieldId
          );

          const newFields = [...section.fields];
          const field = newFields[fieldIndex];
          newFields.splice(fieldIndex, 1);
          newFields.splice(
            action.payload.up ? fieldIndex - 1 : fieldIndex + 1,
            0,
            field
          );

          return { ...section, fields: newFields };
        }),
      };
    case "SPLIT_SECTION": {
      const sectionToSplit = state.sections.find(
        (section) => section.id === action.payload.sectionId
      );
      if (!sectionToSplit) return state;

      const fieldIndex = sectionToSplit.fields.findIndex(
        (field) => field.id === action.payload.fieldId
      );
      if (fieldIndex === -1) return state;

      // Create fields for new section (all fields after the split point)
      const fieldsForNewSection = sectionToSplit.fields.slice(fieldIndex + 1);
      const fieldsForOriginalSection = sectionToSplit.fields.slice(0, fieldIndex + 1);

      // Create new section
      const newSection: Section = {
        id: uuidv4(),
        title: `${sectionToSplit.title} (Split)`,
        fields: fieldsForNewSection,
        isStartingSection: false,
        nextSectionId: sectionToSplit.nextSectionId
      };

      // Update sections array
      const sectionIndex = state.sections.findIndex(
        (section) => section.id === action.payload.sectionId
      );
      const updatedSections = [...state.sections];

      // Update original section's fields and next section
      updatedSections[sectionIndex] = {
        ...sectionToSplit,
        fields: fieldsForOriginalSection,
        nextSectionId: newSection.id
      };

      // Insert new section after the original one
      updatedSections.splice(sectionIndex + 1, 0, newSection);

      return {
        ...state,
        sections: updatedSections
      };
    }
    case "REPLACE_FORM_DEFINITION":
      return action.payload;

    default:
      throw new Error("Invalid action type");
  }
}

function initializeBranchingOptions(
  sections: Section[],
  fieldId: string,
  currentSection: Section
): string[] {
  const field = currentSection.fields.find((field) => field.id === fieldId);
  if (!field || !field.options) return [];

  const currentIndex = sections.findIndex(
    (section) => section.id === currentSection.id
  );

  const nextSection =
    currentIndex === sections.length - 1
      ? "end"
      : sections[currentIndex + 1].id;

  return field.options.map(() => nextSection);
}
